import "../Resources/Css/common.css";
import "../Resources/Css/main.css";
import React, { useState, useEffect } from "react";
import styled from "styled-components";

import {
  GetNewestNews,
  GetFAQData,
  ShowPopupCount,
  ShowPopup,
  getNewsThumbnail,
} from "./Api/callData.js";

/* ────────────────────────────────────────────────────────────────────────────
 * Styled primitives — consistent with Business/cogame.js and Business/b-playground.js
 * ────────────────────────────────────────────────────────────────────────── */
const Page = styled.div`
  background: #ffffff;
  color: #0f172a;
  font-family: "Pretendard Variable", Pretendard, -apple-system, BlinkMacSystemFont,
    "Segoe UI", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Section = styled.section`
  display: block;
  min-height: auto;
  padding: clamp(72px, 9vw, 120px) 0;
  background: ${(p) => p.$bg || "#ffffff"};
  border-top: ${(p) => (p.$divider ? "1px solid #f1f5f9" : "none")};
`;

const Eyebrow = styled.p`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #6366f1;
  margin: 0 0 16px;
  text-align: center;
`;

const Heading = styled.h2`
  font-size: clamp(32px, 4.2vw, 52px);
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: #0f172a;
  margin: 0 0 20px;
  text-align: center;
`;

const Lead = styled.p`
  font-size: clamp(16px, 1.4vw, 19px);
  line-height: 1.65;
  color: #64748b;
  margin: 0 auto;
  max-width: 640px;
  text-align: center;
`;

const SectionHeader = styled.div`
  margin-bottom: 56px;
  text-align: center;
`;

/* ────────────────────────────────────────────────────────────────────────────
 * Hero
 * ────────────────────────────────────────────────────────────────────────── */
const Hero = styled.section`
  display: block;
  min-height: auto;
  margin-top: calc(-1 * clamp(60px, 10vw, 120px));
  padding: calc(clamp(96px, 12vw, 160px) + clamp(60px, 10vw, 120px)) 0
    clamp(72px, 9vw, 100px);
  text-align: center;
  background:
    radial-gradient(ellipse 70% 50% at 50% 0%, #eef2ff 0%, transparent 70%),
    #ffffff;
  position: relative;
  overflow: hidden;
`;

const HeroInner = styled.div`
  max-width: 920px;
  margin: 0 auto;
  padding: 0 24px;
`;

const HeroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 999px;
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.2);
  color: #4338ca;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  margin-bottom: 28px;

  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #6366f1;
  }
`;

const HeroTitle = styled.h1`
  font-size: clamp(44px, 6.5vw, 88px);
  font-weight: 700;
  line-height: 1.02;
  letter-spacing: -0.04em;
  color: #0f172a;
  margin: 0 0 28px;

  span.accent {
    background: linear-gradient(135deg, #6366f1 0%, #a855f7 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const HeroLead = styled.p`
  font-size: clamp(17px, 1.7vw, 22px);
  line-height: 1.55;
  color: #475569;
  margin: 0 auto 40px;
  max-width: 680px;
`;

const CTARow = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 26px;
  background: #0f172a;
  color: #ffffff;
  font-weight: 500;
  font-size: 15px;
  border-radius: 10px;
  text-decoration: none;
  transition: background 0.15s ease, transform 0.15s ease;

  &:hover {
    background: #1e293b;
    color: #ffffff;
  }
  &:active {
    transform: translateY(1px);
  }
`;

const GhostButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 14px 22px;
  background: transparent;
  color: #475569;
  font-weight: 500;
  font-size: 15px;
  border-radius: 10px;
  text-decoration: none;
  border: 1px solid #e2e8f0;
  transition: border-color 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: #cbd5e1;
    color: #0f172a;
  }
`;

const HeroImageWrap = styled.div`
  margin: clamp(60px, 8vw, 96px) auto 0;
  max-width: 1100px;
  padding: 0 24px;

  img {
    width: 100%;
    height: auto;
    border-radius: 24px;
    display: block;
    box-shadow: 0 30px 60px -30px rgba(15, 23, 42, 0.15);
  }
`;

/* ────────────────────────────────────────────────────────────────────────────
 * Brain Health Playground feature card (links to /business/b-playground)
 * ────────────────────────────────────────────────────────────────────────── */
const BHPCard = styled.a`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  border: 1px solid #ede9fe;
  border-radius: 24px;
  overflow: hidden;
  text-decoration: none;
  background:
    radial-gradient(ellipse 80% 60% at 0% 0%, #f5f3ff 0%, transparent 70%),
    #ffffff;
  transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: #c4b5fd;
    transform: translateY(-4px);
    box-shadow: 0 24px 50px -20px rgba(124, 58, 237, 0.25);
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const BHPCardLeft = styled.div`
  padding: clamp(40px, 5vw, 56px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;

  .wordmark {
    width: 100%;
    max-width: 260px;
    height: auto;
    margin-bottom: 8px;
  }
  p.desc {
    font-size: 16px;
    line-height: 1.65;
    color: #475569;
    margin: 0;
  }
  .arrow {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 12px;
    color: #6d28d9;
    font-weight: 600;
    font-size: 15px;
  }
`;

const BHPCardRight = styled.div`
  position: relative;
  background: #faf5ff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(32px, 4vw, 48px);

  img {
    width: 100%;
    max-width: 360px;
    height: auto;
    border-radius: 14px;
    box-shadow: 0 18px 40px -16px rgba(15, 23, 42, 0.25);
  }
`;

/* ────────────────────────────────────────────────────────────────────────────
 * FAQ accordion (dynamic data)
 * ────────────────────────────────────────────────────────────────────────── */
const FAQList = styled.div`
  max-width: 760px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FAQItem = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #ffffff;
  overflow: hidden;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #c7d2fe;
  }

  .q {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 22px 24px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 600;
    color: #0f172a;
    line-height: 1.4;
    user-select: none;
  }
  .icon {
    width: 24px;
    height: 24px;
    color: #6366f1;
    flex-shrink: 0;
    transition: transform 0.2s ease;
  }
  &[data-open="true"] .icon {
    transform: rotate(45deg);
  }
  .a {
    padding: 0 24px 24px;
    font-size: 15px;
    line-height: 1.65;
    color: #64748b;
    border-top: 1px solid #f1f5f9;
    margin-top: 0;
    padding-top: 20px;
    white-space: pre-line;
  }
`;

/* ────────────────────────────────────────────────────────────────────────────
 * Testimonials
 * ────────────────────────────────────────────────────────────────────────── */
const TestGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const TestCard = styled.article`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 32px 30px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  transition: border-color 0.15s ease, transform 0.15s ease;

  &:hover {
    border-color: #c7d2fe;
    transform: translateY(-3px);
  }

  .quote {
    font-size: 56px;
    line-height: 1;
    color: #c7d2fe;
    font-family: Georgia, serif;
    margin: -8px 0 -12px;
  }
  p.body {
    font-size: 15.5px;
    line-height: 1.7;
    color: #334155;
    margin: 0;
  }
  .person {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-top: auto;
  }
  .avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    overflow: hidden;
    background: #e2e8f0;
    flex-shrink: 0;
  }
  .avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .name {
    font-size: 15px;
    font-weight: 600;
    color: #0f172a;
    line-height: 1.3;
  }
  .role {
    font-size: 13px;
    color: #64748b;
    line-height: 1.3;
  }
`;

/* ────────────────────────────────────────────────────────────────────────────
 * News
 * ────────────────────────────────────────────────────────────────────────── */
const NewsHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 40px;
  gap: 24px;

  h2 {
    font-size: clamp(28px, 3.6vw, 42px);
    font-weight: 700;
    letter-spacing: -0.025em;
    color: #0f172a;
    margin: 0;
    line-height: 1.15;
    max-width: 580px;
    text-align: left;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 28px;
  }
`;

const NewsMore = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  white-space: nowrap;
  color: #475569;
  font-weight: 500;
  font-size: 15px;
  text-decoration: none;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 4px;
  transition: color 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: #0f172a;
    border-color: #94a3b8;
  }
`;

const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const NewsCard = styled.a`
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
  text-decoration: none;
  transition: border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #c7d2fe;
    transform: translateY(-3px);
    box-shadow: 0 18px 35px -18px rgba(15, 23, 42, 0.15);
  }

  .cover {
    aspect-ratio: 16 / 10;
    width: 100%;
    object-fit: cover;
    background: #f1f5f9;
    display: block;
  }
  .body {
    padding: 22px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
  }
  .title {
    font-size: 17px;
    font-weight: 600;
    color: #0f172a;
    line-height: 1.4;
    letter-spacing: -0.01em;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .date {
    font-size: 13px;
    color: #94a3b8;
    margin-top: auto;
  }
`;

/* ── Popup modal (replaces legacy window.open) ── */
const PopupOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  animation: popupFadeIn 0.18s ease-out;

  @keyframes popupFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const PopupCard = styled.div`
  position: relative;
  width: 100%;
  max-width: 560px;
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 30px 80px -20px rgba(15, 23, 42, 0.35);
  overflow: hidden;
  animation: popupZoomIn 0.22s ease-out;
  font-family: "Pretendard Variable", Pretendard, -apple-system,
    BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  @keyframes popupZoomIn {
    from {
      transform: translateY(8px) scale(0.97);
      opacity: 0;
    }
    to {
      transform: translateY(0) scale(1);
      opacity: 1;
    }
  }
`;

const PopupClose = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 2;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 0;
  background: rgba(15, 23, 42, 0.55);
  color: #ffffff;
  cursor: pointer;
  font-family: inherit;
  backdrop-filter: blur(4px);
  transition: background 0.15s ease;

  &:hover {
    background: rgba(15, 23, 42, 0.8);
  }
`;

const PopupTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: #0f172a;
  margin: 0;
  padding: 22px 56px 12px 26px;
`;

const PopupBody = styled.div`
  flex: 1;
  overflow: auto;
  padding: 4px 26px 22px;
  font-size: 15px;
  line-height: 1.65;
  color: #334155;

  img {
    max-width: 100%;
    height: auto;
    border-radius: 12px;
  }

  p {
    margin: 0 0 12px;
  }

  a {
    color: #6366f1;
  }
`;

const PopupFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 22px;
  border-top: 1px solid #f1f5f9;
  background: #fafbff;
  flex-wrap: wrap;

  .dismiss {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13.5px;
    color: #64748b;
    cursor: pointer;
    user-select: none;

    input {
      accent-color: #6366f1;
    }
  }

  .pager {
    font-size: 12.5px;
    color: #94a3b8;
  }

  .next {
    padding: 8px 18px;
    border-radius: 999px;
    border: 0;
    background: #0f172a;
    color: #ffffff;
    font-size: 13.5px;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;
    transition: background 0.15s ease;

    &:hover {
      background: #1e293b;
    }
  }
`;

const PopupCloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const DISMISS_KEY_PREFIX = "popup_dismiss_";
const isDismissedToday = (id) => {
  if (typeof window === "undefined") return false;
  const stored = localStorage.getItem(DISMISS_KEY_PREFIX + id);
  if (!stored) return false;
  return stored === new Date().toISOString().split("T")[0];
};
const dismissForToday = (id) => {
  if (typeof window === "undefined") return;
  localStorage.setItem(
    DISMISS_KEY_PREFIX + id,
    new Date().toISOString().split("T")[0]
  );
};

const PlusIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);

const ArrowUpRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 17L17 7M9 7h8v8" />
  </svg>
);


export function Index() {
  const [post, setPost] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [showPopupCount, setShowPopupCount] = useState(0);
  const [popupPost, setPopupPost] = useState([]);
  const [activePopups, setActivePopups] = useState([]);
  const [popupIdx, setPopupIdx] = useState(0);
  const [dismissChecked, setDismissChecked] = useState(false);

  const [activeIndex, setActiveIndex] = useState(null);

  function revealHidden(index) {
    setActiveIndex(activeIndex === index ? null : index);
  }

  // FAQ DB 불러오기
  const loadFAQ = async (page) => {
    setLoading(true);
    const { postsData, lastVisibleDoc } = await GetFAQData(page, lastVisible);
    setPost(postsData);
    setLastVisible(lastVisibleDoc);
    setLoading(false);
  };

  useEffect(() => {
    loadFAQ(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const [newsData, setNewsData] = useState([]);

  useEffect(() => {
    const fetchNewsData = async () => {
      const data = await GetNewestNews();
      setNewsData(data);
    };
    fetchNewsData();
  }, []);

  useEffect(() => {
    const fetchPopupCount = async () => {
      const count = await ShowPopupCount();
      setShowPopupCount(count);
    };
    fetchPopupCount();
  }, []);

  useEffect(() => {
    if (showPopupCount !== 0) {
      const fetchPopupPost = async () => {
        const popupData = await ShowPopup(showPopupCount);
        setPopupPost(popupData);
      };
      fetchPopupPost();
    }
  }, [showPopupCount]);

  useEffect(() => {
    if (popupPost.length === 0) return;
    const visible = popupPost.filter((p) => !isDismissedToday(p.id));
    if (visible.length > 0) {
      setActivePopups(visible);
      setPopupIdx(0);
    }
  }, [popupPost]);

  useEffect(() => {
    setDismissChecked(false);
  }, [popupIdx]);

  useEffect(() => {
    if (activePopups.length === 0) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setActivePopups([]);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [activePopups.length]);

  const currentPopup = activePopups[popupIdx];
  const handleClosePopup = () => {
    if (dismissChecked && currentPopup) {
      dismissForToday(currentPopup.id);
    }
    if (popupIdx + 1 < activePopups.length) {
      setPopupIdx(popupIdx + 1);
    } else {
      setActivePopups([]);
    }
  };

  const topNews = (newsData || []).slice(0, 3);

  return (
    <Page>
      {/* Hero */}
      <Hero>
        <HeroInner>
          <HeroBadge>Brain wellness, powered by AI</HeroBadge>
          <HeroTitle>
            Next-Gen <span className="accent">AI Cognitive</span>
            <br />
            Training Solutions
          </HeroTitle>
          <HeroLead>
            Supporting cognitive wellness through AI and innovative technology —
            designed to engage, support, and empower older adults in everyday
            care.
          </HeroLead>
          <CTARow>
            <PrimaryButton href="/business/b-playground">
              Explore the program
              <ArrowRightIcon />
            </PrimaryButton>
            <GhostButton href="/business/cogame">See Cogame</GhostButton>
          </CTARow>
        </HeroInner>
        <HeroImageWrap>
          <img src="/images/index_title.png" alt="" />
        </HeroImageWrap>
      </Hero>

      {/* BHP feature card */}
      <Section $divider>
        <Container>
          <BHPCard href="/business/b-playground">
            <BHPCardLeft>
              <img
                className="wordmark"
                src="/images/bhp_wordmark.png"
                alt="Brain Health Playground"
              />
              <p className="desc">
                AI motion and voice recognition for dual-tasking cognitive
                wellness — built with cognitive scientists for older adults.
              </p>
              <span className="arrow">
                Learn more <ArrowRightIcon />
              </span>
            </BHPCardLeft>
            <BHPCardRight>
              <img src="/images/main2.png" alt="Brain Health Playground product" />
            </BHPCardRight>
          </BHPCard>
        </Container>
      </Section>

      {/* FAQ */}
      <Section $bg="#fafafa" $divider>
        <Container>
          <SectionHeader>
            <Eyebrow>FAQ</Eyebrow>
            <Heading>Frequently asked questions.</Heading>
            <Lead>
              Common questions about our services, programs, and how Youngand
              supports senior cognitive wellness.
            </Lead>
          </SectionHeader>

          <FAQList>
            {post && post.length > 0 ? (
              post.map((item, index) => {
                const open = activeIndex === index;
                return (
                  <FAQItem key={item.id} data-open={open}>
                    <div className="q" onClick={() => revealHidden(index)}>
                      <span>{item.title}</span>
                      <PlusIcon className="icon" />
                    </div>
                    {open && <div className="a">{item.content}</div>}
                  </FAQItem>
                );
              })
            ) : (
              <p style={{ textAlign: "center", color: "#94a3b8" }}>
                {loading ? "Loading..." : "No posts available"}
              </p>
            )}
          </FAQList>
        </Container>
      </Section>

      {/* Testimonials */}
      <Section $divider>
        <Container>
          <SectionHeader>
            <Eyebrow>Testimonials</Eyebrow>
            <Heading>Trusted by care professionals.</Heading>
            <Lead>
              Discover why our clients trust Youngand — read their stories and
              feedback.
            </Lead>
          </SectionHeader>

          <TestGrid>
            <TestCard>
              <div className="quote">“</div>
              <p className="body">
                Brain Health Playground has been a game-changer in how we
                approach cognitive health support. The system&apos;s use of
                motion detection and AI has allowed us to identify users who may
                be at risk for cognitive decline, leading to earlier monitoring
                and better outcomes.
              </p>
              <div className="person">
                <div className="avatar">
                  <img src="/images/tesimonials_01.jpg" alt="" />
                </div>
                <div>
                  <div className="name">Sarah Thompson</div>
                  <div className="role">Occupational Therapist</div>
                </div>
              </div>
            </TestCard>

            <TestCard>
              <div className="quote">“</div>
              <p className="body">
                Brain Health Playground has made a noticeable difference in our
                residents&apos; cognitive function. The AI-driven exercises are
                engaging and personalized, helping us deliver tailored care with
                impressive results.
              </p>
              <div className="person">
                <div className="avatar">
                  <img src="/images/tesimonials_02.jpg" alt="" />
                </div>
                <div>
                  <div className="name">Alex Kim</div>
                  <div className="role">Senior Care Specialist</div>
                </div>
              </div>
            </TestCard>
          </TestGrid>
        </Container>
      </Section>

      {/* News */}
      <Section $bg="#fafafa" $divider>
        <Container>
          <NewsHeader>
            <h2>In the news</h2>
            <NewsMore href="/community/news">
              View all news <ArrowUpRightIcon />
            </NewsMore>
          </NewsHeader>

          {topNews.length > 0 ? (
            <NewsGrid>
              {topNews.map((news) => (
                <NewsCard key={news.id} href={`/community/news/${news.id}`}>
                  <img
                    className="cover"
                    src={getNewsThumbnail(news)}
                    alt=""
                    loading="lazy"
                  />
                  <div className="body">
                    <div className="title">{news.title}</div>
                    <div className="date">
                      {news.reg_time
                        ? news.reg_time.toDate().toISOString().split("T")[0]
                        : ""}
                    </div>
                  </div>
                </NewsCard>
              ))}
            </NewsGrid>
          ) : (
            <p style={{ textAlign: "center", color: "#94a3b8" }}>Loading...</p>
          )}
        </Container>
      </Section>

      {currentPopup && (
        <PopupOverlay
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setActivePopups([]);
          }}
          role="dialog"
          aria-modal="true"
          aria-label={currentPopup.title || "Popup"}
        >
          <PopupCard>
            <PopupClose
              type="button"
              onClick={() => setActivePopups([])}
              aria-label="Close"
            >
              <PopupCloseIcon />
            </PopupClose>
            {currentPopup.title && <PopupTitle>{currentPopup.title}</PopupTitle>}
            <PopupBody
              dangerouslySetInnerHTML={{ __html: currentPopup.content || "" }}
            />
            <PopupFooter>
              <label className="dismiss">
                <input
                  type="checkbox"
                  checked={dismissChecked}
                  onChange={(e) => setDismissChecked(e.target.checked)}
                />
                Don&apos;t show today
              </label>
              {activePopups.length > 1 && (
                <span className="pager">
                  {popupIdx + 1} / {activePopups.length}
                </span>
              )}
              <button type="button" className="next" onClick={handleClosePopup}>
                {popupIdx + 1 < activePopups.length ? "Next" : "Close"}
              </button>
            </PopupFooter>
          </PopupCard>
        </PopupOverlay>
      )}
    </Page>
  );
}
